import { PlusIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/catalyst/button';
import type { ReadDepartmentRecordsChannel } from '@/services/api/generated/policeDepartmentsService';
import { enhancedApi } from '@/services/api/policeDepartmentsService';

import { ConfirmDialog } from '../../components/ConfirmDialog';
import { RecordsChannelFormModal } from '../RecordsChannelFormModal';
import { ChannelTable } from './ChannelTable';
import { RequestMethodsSectionProps } from './types';

export const RequestMethodsSection = ({
  departmentId,
  channels,
}: RequestMethodsSectionProps) => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingChannel, setEditingChannel] =
    useState<ReadDepartmentRecordsChannel | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{
    open: boolean;
    channelId: string | null;
  }>({
    open: false,
    channelId: null,
  });

  const [deleteChannel, { isLoading: isDeleting }] =
    enhancedApi.usePoliceRecordsChannelsDestroyMutation();

  const handleAddNew = () => {
    setEditingChannel(null);
    setIsFormOpen(true);
  };

  const handleEdit = (channel: ReadDepartmentRecordsChannel) => {
    setEditingChannel(channel);
    setIsFormOpen(true);
  };

  const handleDeleteClick = (channelId: string) => {
    setDeleteConfirm({ open: true, channelId });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm.channelId) return;

    try {
      await deleteChannel({
        departmentId,
        id: deleteConfirm.channelId,
      }).unwrap();

      toast.success('Records channel deleted');
      setDeleteConfirm({ open: false, channelId: null });
    } catch (error: any) {
      // Check for the specific error when trying to delete the last mail channel with contacts
      if (
        error?.data?.reason === 'last_mail_channel_with_contacts' &&
        error?.data?.contact_count
      ) {
        const contactCount = error.data.contact_count;
        toast.error(
          `Cannot delete the last mailing address channel because it has ${contactCount} postgrid ${
            contactCount === 1 ? 'contact' : 'contacts'
          }. Create another mail channel first, or transfer the contacts.`,
          {
            duration: 6000,
          },
        );
      } else {
        // Generic error
        const message =
          error?.data?.error || 'Failed to delete records channel';
        toast.error(message);
      }
    }
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setEditingChannel(null);
  };

  return (
    <>
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <div>
            <h2 className="text-base font-semibold text-gray-900">
              Request Methods
            </h2>
            <p className="mt-0.5 text-sm text-gray-500">
              Configure how records can be requested from this department
            </p>
          </div>
          <Button
            onClick={handleAddNew}
            className="flex items-center gap-2"
            color="blue"
          >
            <PlusIcon className="h-4 w-4" />
            Add Method
          </Button>
        </div>

        <div className="p-6">
          {channels.length === 0 ? (
            <div className="flex flex-col items-center py-12 text-center">
              <div className="rounded-full bg-gray-100 p-4">
                <PlusIcon className="h-10 w-10 text-gray-400" />
              </div>
              <h3 className="mt-4 text-base font-semibold text-gray-900">
                No request methods configured
              </h3>
              <p className="mt-2 max-w-sm text-sm text-gray-500">
                Add channels to define how records can be requested from this
                police department. You can configure online portals, phone
                contacts, email, or mail request methods.
              </p>
              <Button
                onClick={handleAddNew}
                className="mt-6 flex items-center gap-2"
                color="blue"
              >
                <PlusIcon className="h-4 w-4" />
                Add Your First Method
              </Button>
            </div>
          ) : (
            <ChannelTable
              channels={channels}
              onEdit={handleEdit}
              onDelete={handleDeleteClick}
            />
          )}
        </div>
      </div>

      {isFormOpen && (
        <RecordsChannelFormModal
          departmentId={departmentId}
          channel={editingChannel}
          onClose={handleFormClose}
        />
      )}

      <ConfirmDialog
        open={deleteConfirm.open}
        onOpenChange={(open) => setDeleteConfirm({ open, channelId: null })}
        onConfirm={handleDeleteConfirm}
        title="Delete Records Channel"
        description="This will permanently remove this records request channel. This action cannot be undone."
        confirmText="Delete"
        isLoading={isDeleting}
        variant="destructive"
      />
    </>
  );
};
